import type { ComponentProps, FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'gold' | 'primary' | 'outline' | 'outline-light'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonLinkProps extends ComponentProps<typeof Link> {
    variant?: ButtonVariant
    size?: ButtonSize
}

const VARIANTS: Record<ButtonVariant, string> = {
    'gold': 'bg-gold text-forest-950 shadow-[0_14px_34px_-16px_rgba(201,162,74,0.85)] hover:bg-gold-light',
    'primary': 'bg-forest-700 text-cream shadow-[0_14px_34px_-18px_rgba(28,74,53,0.9)] hover:bg-forest-800',
    'outline': 'border border-forest-700/25 text-forest-700 hover:border-forest-700 hover:bg-forest-700/5',
    'outline-light': 'border border-cream/25 text-cream hover:border-gold hover:text-gold'
}

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-10 px-4 text-sm',
    md: 'h-12 px-6 text-[0.95rem]',
    lg: 'h-14 px-8 text-base'
}

export const buttonClasses = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string => {
    return cn(
        'inline-flex shrink-0 items-center justify-center gap-2 rounded-full font-semibold whitespace-nowrap transition-all duration-200 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60 [&_svg]:size-[1.15em]',
        VARIANTS[variant],
        SIZES[size],
        className
    )
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    variant,
    size,
    className,
    ...props
}): ReactNode => {
    return <Link className={buttonClasses(variant, size, className)} {...props} />
}
